/**
 * Athlete Utilities - Helper Functions
 * 
 * Shared utility functions for athlete-related operations.
 * Extracted from consolidated athleteService.ts for better organization.
 * 
 * Created: 2025-01-27 (Refactored from athleteService.ts)
 */

// ========================================
// VALIDATION UTILITIES
// ========================================

/**
 * Utility function to validate UUID format
 */
export function isValidUuid(id: string): boolean {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(id);
}

// ========================================
// DATE AND AGE UTILITIES
// ========================================

/**
 * Local calendar date from year/month/day. The field-by-field constructor
 * never rejects — `new Date(2011, 12, 45)` is a real date in February 2012 —
 * so parts that do not round-trip stay invalid (#4181 dashed, #4192 slash).
 */
function dateFromCalendarParts(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Parse an athlete birthdate string into a local calendar Date.
 * Shared by calculateAge and extractBirthYear so slash and dashed
 * shapes cannot drift (#4181, #4192, #4199).
 */
function parseAthleteBirthDate(dateOfBirth: string): Date | null {
  if (dateOfBirth.includes('/') && dateOfBirth.split('/').length === 3) {
    const [month, day, year] = dateOfBirth.split('/').map(Number);
    return dateFromCalendarParts(year, month, day);
  }

  // Dashed dates parse field by field, the same semantics as
  // `parseDateLocal` — `new Date('2011-04-02')` is UTC midnight, the
  // PREVIOUS day everywhere west of Greenwich, so every age gate reading
  // this was off by one on the boundary day for US gyms (#4181).
  //
  // TWO dashed shapes arrive here, which is why this does not simply call
  // parseDateLocal: YYYY-MM-DD (what DateOfBirthField computes) and
  // MM-DD-YYYY (the field's own typed mask — its placeholder). The year
  // says which end it is on.
  const parts = dateOfBirth.split(/[T\s]/)[0].split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null;
  }
  const [year, month, day] = parts[0] >= 1000 ? parts : [parts[2], parts[0], parts[1]];
  return dateFromCalendarParts(year, month, day);
}

/**
 * Age in whole years. `asOfIsoDate` (YYYY-MM-DD) is the day to measure on —
 * pass the gym's local date on the server, where the process clock is UTC and
 * would add a birthday year early for US gyms in the evening. Defaults to the
 * device's local today.
 */
export function calculateAge(dateOfBirth: string, asOfIsoDate?: string): number {
  if (!dateOfBirth) return 0;

  const birthDate = parseAthleteBirthDate(dateOfBirth);
  if (!birthDate || isNaN(birthDate.getTime())) {
    return -1;
  }

  const today = (asOfIsoDate && parseAthleteBirthDate(asOfIsoDate)) || new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Calendar birth year from a date-of-birth string.
 *
 * Do not use `new Date(iso).getFullYear()`: `2012-01-01` is midnight UTC,
 * which is Dec 31, 2011 in every US timezone, so January 1st birthdays
 * read as the previous year on gym machines (#4199). USASF placement is
 * year-based — this extracts the year only; it does not change the grid.
 */
export function extractBirthYear(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;
  const birthDate = parseAthleteBirthDate(dateOfBirth);
  if (!birthDate) return null;
  return birthDate.getFullYear();
} 